import React, { useState, useEffect } from 'react';
import {
  TagIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowsRightLeftIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import Button from '../../../components/ui/Button';
import Card, { CardHeader, CardContent } from '../../../components/ui/Card';
import Modal, { ModalContent, ModalFooter } from '../../../components/ui/Modal';
import FormInput from '../../../components/ui/FormInput';
import { postsService } from '../../../services/postsService';

interface TagStats {
  tag: string;
  count: number;
  postIds: string[];
}

/**
 * Tag Manager Page
 * Allows creators to organize, rename, merge, and delete content tags
 */
const TagManagerPage: React.FC = () => {
  const [tags, setTags] = useState<TagStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'count'>('count');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // Modals
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Form data
  const [renameData, setRenameData] = useState({ oldTag: '', newTag: '' });
  const [mergeTarget, setMergeTarget] = useState('');
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    try {
      // Load all creator's posts to extract tag statistics
      const response = await postsService.list({ limit: 1000 });
      const posts = response.posts || [];
      
      // Calculate tag statistics
      const tagMap = new Map<string, TagStats>();
      
      posts.forEach((post: any) => {
        if (post.tags && Array.isArray(post.tags)) {
          post.tags.forEach((tag: string) => {
            if (tag.trim()) {
              const existing = tagMap.get(tag);
              if (existing) {
                existing.count++;
                existing.postIds.push(post.id);
              } else {
                tagMap.set(tag, {
                  tag,
                  count: 1,
                  postIds: [post.id],
                });
              }
            }
          });
        }
      });
      
      setTags(Array.from(tagMap.values()));
    } catch (error) {
      console.error('Failed to load tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRenameTag = async () => {
    if (!renameData.oldTag || !renameData.newTag) {
      alert('Please enter both old and new tag names');
      return;
    }

    setIsProcessing(true);
    try {
      const tagToRename = tags.find(t => t.tag === renameData.oldTag);
      if (!tagToRename) return;

      // Update all posts with this tag
      for (const postId of tagToRename.postIds) {
        const post = await postsService.get(postId);
        const newTags = post.tags.map((t: string) => 
          t === renameData.oldTag ? renameData.newTag : t
        );
        await postsService.update(postId, { tags: newTags } as any);
      }

      await loadTags();
      setShowRenameModal(false);
      setRenameData({ oldTag: '', newTag: '' });
      alert(`Successfully renamed "${renameData.oldTag}" to "${renameData.newTag}"`);
    } catch (error) {
      console.error('Failed to rename tag:', error);
      alert('Failed to rename tag. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMergeTags = async () => {
    if (selectedTags.length < 2) {
      alert('Please select at least 2 tags to merge');
      return;
    }
    if (!mergeTarget) {
      alert('Please enter a target tag name');
      return;
    }

    setIsProcessing(true);
    try {
      // Get all posts that have any of the selected tags
      const postsToUpdate = new Set<string>();
      selectedTags.forEach(tag => {
        const tagStats = tags.find(t => t.tag === tag);
        if (tagStats) {
          tagStats.postIds.forEach(id => postsToUpdate.add(id));
        }
      });

      // Update each post
      for (const postId of postsToUpdate) {
        const post = await postsService.get(postId);
        const newTags = new Set<string>();
        
        post.tags.forEach((tag: string) => {
          if (selectedTags.includes(tag)) {
            newTags.add(mergeTarget);
          } else {
            newTags.add(tag);
          }
        });
        
        await postsService.update(postId, { tags: Array.from(newTags) } as any);
      }

      await loadTags();
      setShowMergeModal(false);
      setSelectedTags([]);
      setMergeTarget('');
      alert(`Successfully merged ${selectedTags.length} tags into "${mergeTarget}"`);
    } catch (error) {
      console.error('Failed to merge tags:', error);
      alert('Failed to merge tags. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTags = async () => {
    if (selectedTags.length === 0) {
      alert('Please select tags to delete');
      return;
    }

    setIsProcessing(true);
    try {
      // Get all posts that have any of the selected tags
      const postsToUpdate = new Set<string>();
      selectedTags.forEach(tag => {
        const tagStats = tags.find(t => t.tag === tag);
        if (tagStats) {
          tagStats.postIds.forEach(id => postsToUpdate.add(id));
        }
      });

      // Remove tags from each post
      for (const postId of postsToUpdate) {
        const post = await postsService.get(postId);
        const newTags = post.tags.filter((tag: string) => !selectedTags.includes(tag));
        await postsService.update(postId, { tags: newTags } as any);
      }

      await loadTags();
      setShowDeleteModal(false);
      setSelectedTags([]);
      alert(`Successfully deleted ${selectedTags.length} tag(s)`);
    } catch (error) {
      console.error('Failed to delete tags:', error);
      alert('Failed to delete tags. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleTagSelection = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const selectAllTags = () => {
    const filtered = getFilteredTags();
    setSelectedTags(filtered.map(t => t.tag));
  };

  const deselectAllTags = () => {
    setSelectedTags([]);
  };

  const getFilteredTags = () => {
    let filtered = [...tags];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.tag.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.tag.localeCompare(b.tag));
    } else {
      filtered.sort((a, b) => b.count - a.count);
    }

    return filtered;
  };

  const filteredTags = getFilteredTags();
  const totalPosts = tags.reduce((sum, t) => sum + t.count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-patreon-orange"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tag Manager</h1>
          <p className="text-gray-600 mt-1">
            Organize and manage your content tags
          </p>
        </div>
        <Button onClick={loadTags}>
          <ArrowsRightLeftIcon className="w-5 h-5 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tags</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{tags.length}</p>
              </div>
              <TagIcon className="w-12 h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tagged Posts</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalPosts}</p>
              </div>
              <TagIcon className="w-12 h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Selected</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{selectedTags.length}</p>
              </div>
              <TagIcon className="w-12 h-12 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                />
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-600" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'count')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
              >
                <option value="count">Most Used</option>
                <option value="name">Alphabetical</option>
              </select>
            </div>

            {/* Bulk Actions */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllTags}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAllTags}>
                Deselect All
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          {selectedTags.length > 0 && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (selectedTags.length === 1) {
                    setRenameData({ oldTag: selectedTags[0], newTag: '' });
                    setShowRenameModal(true);
                  } else {
                    alert('Please select exactly one tag to rename');
                  }
                }}
                disabled={selectedTags.length !== 1}
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Rename
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMergeModal(true)}
                disabled={selectedTags.length < 2}
              >
                <ArrowsRightLeftIcon className="w-4 h-4 mr-2" />
                Merge
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tags List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Tags ({filteredTags.length})</h2>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTags.length === 0 ? (
            <div className="text-center py-12">
              <TagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No tags found</p>
              <p className="text-sm text-gray-500 mt-1">
                {searchTerm ? 'Try a different search term' : 'Start tagging your posts to see them here'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTags.map((tagStat) => (
                <div
                  key={tagStat.tag}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    selectedTags.includes(tagStat.tag)
                      ? 'border-patreon-orange bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleTagSelection(tagStat.tag)}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tagStat.tag)}
                      onChange={() => {}}
                      className="w-5 h-5 text-patreon-orange rounded focus:ring-patreon-orange"
                    />
                    <TagIcon className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">{tagStat.tag}</p>
                      <p className="text-sm text-gray-600">
                        Used in {tagStat.count} post{tagStat.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      {tagStat.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rename Modal */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename Tag"
      >
        <ModalContent>
          <div className="space-y-4">
            <p className="text-gray-600">
              This will rename the tag across all {renameData.oldTag && tags.find(t => t.tag === renameData.oldTag)?.count} post(s) that use it.
            </p>
            <FormInput
              label="Current Tag Name"
              value={renameData.oldTag}
              disabled
            />
            <FormInput
              label="New Tag Name"
              value={renameData.newTag}
              onChange={(e) => setRenameData(prev => ({ ...prev, newTag: e.target.value }))}
              placeholder="Enter new tag name"
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowRenameModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleRenameTag} loading={isProcessing}>
            Rename Tag
          </Button>
        </ModalFooter>
      </Modal>

      {/* Merge Modal */}
      <Modal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        title="Merge Tags"
      >
        <ModalContent>
          <div className="space-y-4">
            <p className="text-gray-600">
              Merge {selectedTags.length} selected tags into a single tag.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Selected Tags:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <FormInput
              label="Merge Into"
              value={mergeTarget}
              onChange={(e) => setMergeTarget(e.target.value)}
              placeholder="Enter target tag name"
              helperText="All selected tags will be replaced with this tag"
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowMergeModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleMergeTags} loading={isProcessing}>
            Merge Tags
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tags"
      >
        <ModalContent>
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete {selectedTags.length} tag(s)? This will remove them from all posts.
            </p>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-2">Tags to Delete:</p>
              <div className="flex flex-wrap gap-2">
                {selectedTags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white border border-red-300 rounded-full text-sm text-red-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-500">
              This action cannot be undone.
            </p>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTags} loading={isProcessing}>
            Delete Tags
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TagManagerPage;
